// istanbul ignore file
import { SeatSelectionBusModal, SeatSelectionTrainModal } from '@distribusion/owl-seats'
import { useField, useFormikContext } from 'formik'
import React, { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import config from '@config'
import amplitude from '@lib/analytics/amplitude'
import ancillaryUtils from '@lib/ancillary'
import { ApiError } from '@lib/api'
import bem from '@lib/bem'
import { useTranslation } from '@lib/i18n'
import seatsUtils from '@lib/seatSelection'
import utils from '@lib/utils'
import { CheckoutFormData } from '@pages/Checkout/hooks/useInitialFormValues'
import usePreselectedSeats from '@pages/Checkout/hooks/usePreselectedSeats'
import Preview from '@pages/Checkout/SeatSelection/Seats/Preview'
import useSeatsQueries, { UseSeatsQueriesHook } from '@queries/seats'
import useTwoStepSeatsQuery from '@queries/seats/twoSteps'
import { useSettings } from '@queries/settings'
import { useCheckout } from '@stores/checkout'
import { useParams } from '@stores/params'

import '@pages/Checkout/SeatSelection/Seats/index.scss'

export interface SeatsProps {
  onError?: (error: ErrorCode | null) => void
  enabled?: boolean
  disabled?: boolean
  isConnectionsLoading: boolean
}

type CheckoutSeats = CheckoutFormData['seats']

const getSeatsAmount = (seats?: Seat.BySegment | undefined): number => {
  if (!seats || !Object.values(seats).length) return 0

  return Object.values(seats)[0].length
}

const getError = (
  outbound: UseSeatsQueriesHook,
  inbound: UseSeatsQueriesHook,
  isReturn: boolean,
): ApiError | null | undefined => {
  const [outboundSeats, outboundMeta] = outbound
  const [inboundSeats, inboundMeta] = inbound

  const statuses = [...outboundMeta.status, ...inboundMeta.status]
  const isPending = statuses.includes('pending')

  if (statuses.length === 0 || isPending) return null

  const isEmpty = outboundSeats.length === 0 || (isReturn && inboundSeats.length === 0)
  const error = outboundMeta.error || inboundMeta.error

  const emptyError = isEmpty
    ? new ApiError({
        code: 'emptyResult',
        data: { message: 'Seats layout is empty' },
        status: 204,
        path: '',
      })
    : null

  return error ?? emptyError
}

const Seats = ({ onError, isConnectionsLoading, enabled, disabled }: SeatsProps): ReactElement => {
  const { t } = useTranslation()

  const [opened, setOpened] = useState<boolean>(false)
  const [{ retailerPartnerNumber, marketingCarrierCode }] = useParams()
  const [{ outbound, inbound }] = useCheckout()

  const [{ seatSelection }] = useSettings()
  const { enabledOnCheckout, flowType, sendFareClass } = seatSelection

  const { values, setFieldValue } = useFormikContext<CheckoutFormData>()
  const { seats, passengers, ancillaries, fareClass, vacancy, isVacancyLoading, meta } = values
  const vacancyPrice = vacancy?.price as Money | undefined
  // TODO: Use correct fare for return trip: https://distribusion.atlassian.net/browse/OWL-3957
  const tripFareClasses = useMemo(() => ({ outbound: fareClass, inbound: fareClass }), [fareClass])
  const prevVacancy = useRef(vacancy)

  const loadEnabled = (enabledOnCheckout && values.vacancy?.vacant) || !!enabled
  const isTrain = outbound?.segments[0].vehicle?.vehicleType?.code === 'TRAIN'

  const seatsOptions = {
    fareClass: isTrain || sendFareClass ? fareClass : null,
    cards: meta.cards,
    passengers,
  }

  const outboundSeatsQueries = useSeatsQueries({
    connections: outbound ? [outbound] : undefined,
    enabled: flowType === 'single_step' && loadEnabled,
    options: seatsOptions,
  })
  const inboundSeatsQueries = useSeatsQueries({
    connections: inbound ? [inbound] : undefined,
    enabled: flowType === 'single_step' && loadEnabled,
    options: seatsOptions,
  })
  const trainSeatsTwoStep = useTwoStepSeatsQuery({
    connections: utils.object.compact({ outbound, inbound }),
    enabled: flowType === 'two_step' && loadEnabled,
    options: seatsOptions,
  })

  const [outboundSeats, outboundSeatsMeta] = outboundSeatsQueries
  const [inboundSeats, inboundSeatsMeta] = inboundSeatsQueries

  const isLoading =
    flowType === 'single_step' ? outboundSeatsMeta.isLoading || inboundSeatsMeta.isLoading : trainSeatsTwoStep.isLoading

  usePreselectedSeats(outboundSeats, inboundSeats)

  const layout = useMemo(
    () => ({ outbound: outboundSeats ?? [], inbound: inboundSeats ?? [] }),
    [outboundSeats, inboundSeats],
  )

  const isAncillary = config.seatAncillaryAsSelection[marketingCarrierCode!]
  const seatAncillary = ancillaryUtils.getByCategory('SEAT', vacancy?.ancillaries)[0]
  const trainSeats = useMemo(() => seatsUtils.transformIntoTrainSeats(layout), [layout])
  const mandatory = seatSelection.mandatory || (isAncillary && ancillaries.SEAT.some(ancillaryUtils.isMandatory))
  const hasSeatMaps = !!(flowType === 'single_step'
    ? outboundSeats.length || inboundSeats.length
    : trainSeatsTwoStep.scheme.outbound.length || trainSeatsTwoStep.scheme.inbound.length)

  const seatsLoading = isLoading || isConnectionsLoading || (isAncillary && isVacancyLoading)
  const showModal = hasSeatMaps && !isLoading && loadEnabled && !disabled

  const resetSeatSelection = useCallback(() => {
    setFieldValue('seats', null)
    setOpened(false)
  }, [setFieldValue])

  const handleConfirmSelection = ({ seats }: Seat.SubmitData): void => {
    setFieldValue('seats', seats)
    setOpened(false)
  }

  const handleOpenModal = () => {
    if (config.forcedExtras?.[retailerPartnerNumber]?.seatSelection?.enabled) {
      amplitude.checkout.changeAncillary('Seat', passengers.length)
      return false
    }

    setOpened(true)
    return true
  }

  const validate = useCallback(
    (value: CheckoutSeats): string | undefined => {
      const isValidationIrrelevant = !enabledOnCheckout || !value?.outbound || !hasSeatMaps

      if (!isLoading && !hasSeatMaps) return
      if (isValidationIrrelevant && !mandatory) return

      const divider = Number(!!inbound) + 1
      const amount = getSeatsAmount(value?.outbound) + getSeatsAmount(value?.inbound)

      return amount / divider === passengers.length ? undefined : t('errors.mismatchSeats')
    },
    [enabledOnCheckout, hasSeatMaps, inbound, isLoading, mandatory, passengers.length, t],
  )

  useField({ name: 'seats', validate })

  useEffect(() => {
    if (!hasSeatMaps && (!isAncillary || !seatAncillary)) {
      const error =
        flowType === 'single_step'
          ? getError(outboundSeatsQueries, inboundSeatsQueries, !!inbound)
          : trainSeatsTwoStep.error

      if (error) onError?.(error.code)
    }
  }, [
    flowType,
    hasSeatMaps,
    inboundSeatsQueries,
    isAncillary,
    onError,
    outboundSeatsQueries,
    seatAncillary,
    trainSeatsTwoStep.error,
    inbound,
  ])

  useEffect(() => {
    if (isAncillary && prevVacancy.current !== vacancy) {
      resetSeatSelection()
      prevVacancy.current = vacancy
    }
  }, [isAncillary, prevVacancy, resetSeatSelection, vacancy])

  useEffect(() => {
    if (seatAncillary && ancillaryUtils.isMandatory(seatAncillary) && !ancillaries.SEAT.includes(seatAncillary)) {
      setFieldValue('ancillaries.SEAT', [seatAncillary])
    }
  }, [seatAncillary, ancillaries.SEAT, setFieldValue])

  useEffect(() => {
    if (
      isAncillary &&
      seatAncillary &&
      !ancillaryUtils.isMandatory(seatAncillary) &&
      !ancillaries.SEAT.includes(seatAncillary)
    ) {
      setFieldValue('seats', { inbound: {}, outbound: {} })
    }
  }, [ancillaries.SEAT, isAncillary, seatAncillary, setFieldValue])

  return (
    <div className={bem('extras', 'section')}>
      <Preview
        onOpenModal={handleOpenModal}
        outbound={outbound}
        inbound={inbound}
        ancillary={isAncillary ? seatAncillary : undefined}
        disabled={disabled || !loadEnabled || seatsLoading}
        hasSeatMaps={hasSeatMaps}
        seatsLoading={seatsLoading}
      />
      {showModal && (
        <>
          {flowType === 'single_step' && !isTrain && opened && (
            <SeatSelectionBusModal
              initialSelection={seats}
              layout={layout}
              connections={{ outbound, inbound }}
              reservedSeatsCount={passengers.length}
              initialFare={fareClass}
              opened
              onClose={() => setOpened(false)}
              fareFilter={fareClass}
              onSubmit={handleConfirmSelection}
              outboundSeatsCount={passengers.length}
              passengers={passengers}
            />
          )}
          {flowType === 'single_step' && isTrain && opened && (
            <SeatSelectionTrainModal
              {...trainSeats}
              allowSkip
              opened
              initialSelection={seats}
              reservedSeatsCount={passengers.length}
              onSubmit={handleConfirmSelection}
              onClose={() => setOpened(false)}
              connection={outbound}
              selectedFares={tripFareClasses}
              vacancyPrice={vacancyPrice}
            />
          )}
          {flowType === 'two_step' && opened && (
            <SeatSelectionTrainModal
              initialSelection={seats ?? trainSeatsTwoStep.preselectedSeats}
              layoutQuery={trainSeatsTwoStep.layoutQuery}
              scheme={trainSeatsTwoStep.scheme}
              opened
              reservedSeatsCount={passengers.length}
              onSubmit={handleConfirmSelection}
              onClose={() => setOpened(false)}
              connection={outbound!}
              selectedFares={tripFareClasses}
              onClear={resetSeatSelection}
              vacancyPrice={vacancyPrice}
            />
          )}
        </>
      )}
    </div>
  )
}

export default Seats
